import cn from 'classnames'
import Subnav from 'ext-ui/components/Subnav/Subnav'
import React, { ReactElement, ReactNode } from 'react'
import Lnk from 'src/navigation/Lnk'

import style from './FamilyLayout.style'

/**
 * A single tab in a {@link FamilyLayout}. Routing-free: the parent page decides
 * where the tab points (`to`) and which tab is active (via `activeTabKey`).
 */
export interface FamilyLayoutTab {
  /** Stable identifier, compared against {@link FamilyLayoutProps.activeTabKey}. */
  key: string
  /** Already-translated label rendered inside the tab. */
  label: ReactNode
  /** Destination URL. Navigated via the app's `Lnk` (SPA, back/forward safe). */
  to: string
  /** Translation key for the tab link's accessible name. */
  ariaLabelSource: string
  /** Optional count pill (e.g. unread counts). */
  count?: number
  /** Show a loading shimmer in place of the count pill. */
  countLoading?: boolean
  /** Optional test id placed on the tab link. */
  testId?: string
}

export interface FamilyLayoutProps {
  /** Tab descriptors, in display order. */
  tabs: FamilyLayoutTab[]
  /** Key of the active tab; owned by the routing page. */
  activeTabKey?: string
  /** Align the tab bar with Header/AccountHeader content and keep it on white. */
  alignWithHeader?: boolean
  /** Routed tab content. Swaps beneath the persistent tab bar. */
  children?: ReactNode
  className?: string
}

/**
 * FamilyLayout — the canonical tabbed-family page layout primitive (EX-39748).
 *
 * Renders a persistent tab bar via the design-system `Subnav` (whose
 * `.Subnav-activeBar` slides between tabs) and a content outlet beneath it.
 *
 * The animation is preserved by KEEPING THIS LAYOUT MOUNTED across tab
 * navigation: the routing page that owns the flat route is registered once for
 * the whole family, so React keeps the page — and this Subnav — mounted while
 * only the routed `children` swap. Pushing the tab bar down into per-tab content
 * (so it remounts each navigation) would reset `useSlidingNavBar` state and make
 * the bar snap instead of slide.
 *
 * Routing-free by contract: it receives tab descriptors + the active key as
 * props and renders links; it never reads location/params/router state. The
 * parent page resolves the active tab and the per-tab URLs.
 */
const FamilyLayout = ({
  tabs,
  activeTabKey,
  alignWithHeader = false,
  children,
  className,
}: FamilyLayoutProps): ReactElement => {
  return (
    <div
      className={cn('FamilyLayout', className, {
        'FamilyLayout-alignWithHeader': alignWithHeader,
      })}
      css={style}
    >
      <Subnav
        tabs={tabs.map((tab) => ({
          active: tab.key === activeTabKey,
          count: tab.count,
          countLoading: tab.countLoading,
          children: (
            <Lnk
              className="FamilyLayout-tabLink"
              to={tab.to}
              testId={tab.testId}
              ariaLabelSource={tab.ariaLabelSource}
              ariaCurrent={tab.key === activeTabKey ? 'page' : undefined}
            >
              {tab.label}
            </Lnk>
          ),
        }))}
      />
      {children}
    </div>
  )
}

export default FamilyLayout
